'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import { useIsHydrated } from '@packages/shared/src/hooks/useIsHydrated/useIsHydrated';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { JoeConnectionStatusDocument } from '@packages/gql/generated/shopping/JoeConnectionStatusDocument';
import { JoeLoginUrlDocument } from '@packages/gql/generated/shopping/JoeLoginUrlDocument';
import { JoeEstablishConnectionDocument } from '@packages/gql/generated/shopping/JoeEstablishConnectionDocument';
import { JoeDeleteConnectionDocument } from '@packages/gql/generated/shopping/JoeDeleteConnectionDocument';
import { CustomerDocument } from '@packages/gql/generated/shopping/CustomerDocument';
import { redirectExternal } from '../../utils/externalNavigation';
import type { JoePresentationState } from './JoeOverview.types';
import { resolveJoeState } from './resolveJoeState';
import { joeMessages } from './messages';
import { parseAuthorizationCode, stripAuthorizationCode } from './joeAuthorizationCode';
import { buildJoeConnectUrl } from './joeConnectUrl';

/* GraphQL */ `
  query JoeConnectionStatus($locale: String) {
    customerLoyaltyConnectionStatus(locale: $locale) {
      __typename
      ... on CustomerLoyaltyConnectionStatusPayload {
        status
      }
      ... on CustomerServerFailure {
        failureType
        failureMessage
      }
    }
  }
`;

/* GraphQL */ `
  query JoeLoginUrl {
    customerLoyaltyLoginUrl {
      __typename
      ... on CustomerLoyaltyLoginUrlPayload {
        url
      }
      ... on CustomerServerFailure {
        failureType
        failureMessage
      }
    }
  }
`;

/* GraphQL */ `
  mutation JoeEstablishConnection($code: String!, $locale: String) {
    customerEstablishLoyaltyConnection(code: $code, locale: $locale) {
      __typename
      ... on CustomerLoyaltyConnectionStatusPayload {
        status
      }
      ... on CustomerServerFailure {
        failureType
        failureMessage
      }
    }
  }
`;

/* GraphQL */ `
  mutation JoeDeleteConnection {
    customerDeleteLoyaltyConnection {
      __typename
      ... on CustomerGenericSuccessResult {
        success
      }
      ... on CustomerServerFailure {
        failureType
        failureMessage
      }
    }
  }
`;

const NOTIFICATION_DURATION = 5000;

export type JoeConnectionActions = {
  /** Sends the customer to the jö login. No-op while the login url is unresolved. */
  connect: () => void;
  /** Whether `connect` can do anything yet. */
  canConnect: boolean;
  /** Login url still loading, or the redirect has already been triggered. */
  isConnecting: boolean;
  /** Removes the connection, then refreshes the status. */
  disconnect: () => void;
  /** Delete mutation in flight. */
  isDisconnecting: boolean;
};

export type JoeConnection = JoeConnectionActions & {
  /** Presentation state, `undefined` when no jö UI may render at all. */
  state?: JoePresentationState;
};

/**
 * Data binding for the jö Bonus Club overview UI.
 *
 * Owns the four unified loyalty operations (IOGZ-445) and turns them into the presentation state
 * plus the two customer actions. Everything runs through GraphQL; the legacy REST loyalty client is
 * deliberately not used.
 *
 * Gated on the tenant flag `order.joe.enabled` (true for universalat only); with it off no loyalty
 * request is sent at all. The customer's `loyaltyIntegrationAllowed` only gates linking: the api
 * reports `false` for employees and for customers who are already linked, so an established
 * connection is shown regardless of it.
 */
export const useJoeConnection = (): JoeConnection => {
  const { formatMessage, locale } = useIntl();
  const {
    order: {
      joe: { enabled: isJoeEnabled },
      paths: { myAccountJoeConnect },
    },
  } = useConfig();
  const { addNotification } = useNotification();
  const { jwtToken } = useSession();

  // Same document and variables as `CustomerCard`, so urql serves this from the request the card
  // already makes rather than issuing a second one.
  const [
    { data: customerData, error: customerError, fetching: isCustomerFetching },
    refetchCustomer,
  ] = useAuthQuery({
    query: CustomerDocument,
    variables: { locale },
    pause: !isJoeEnabled,
  });

  const customer = customerData?.customer;
  /**
   * Only an explicit `false` excludes linking. Anything else - a `null` from an api with no opinion,
   * a customer query that is still loading, returned nothing or failed outright - stays eligible, so
   * jö keeps the resilience the query-precedence tests pin down: an unrelated customer failure must
   * not take the card down with it. The cost is that an excluded account sees the card until the
   * flag arrives, one round trip of a query this page already makes.
   */
  const isLoyaltyAllowed =
    customer?.__typename !== 'CustomerDataWithStatus' ||
    customer.loyaltyIntegrationAllowed !== false;

  const isLinkingAllowed = isJoeEnabled && isLoyaltyAllowed;

  /**
   * Whether the flag has had its chance to arrive - answered, failed, or came back empty. Rendering
   * stays fail-open, but establishing a connection is not a card that can be redrawn, so the
   * returning round-trip below waits for this instead of racing the query. A failed customer query
   * still counts as resolved: it must not leave a legitimate code unusable.
   */
  const isEligibilityResolved =
    !isCustomerFetching && (customerData !== undefined || customerError !== undefined);

  const [
    { data: statusData, error: statusError, fetching: isStatusFetching, stale: isStatusStale },
    refetchStatus,
  ] = useAuthQuery({
    query: JoeConnectionStatusDocument,
    variables: { locale },
    pause: !isJoeEnabled,
  });

  const statusResult = statusData?.customerLoyaltyConnectionStatus;
  const isConnected =
    statusResult?.__typename === 'CustomerLoyaltyConnectionStatusPayload' &&
    statusResult.status === 'ConnectionEstablished';
  /** A failure payload is not connectable either, so nothing is prefetched for one. */
  const isDisconnected =
    statusResult?.__typename === 'CustomerLoyaltyConnectionStatusPayload' &&
    statusResult.status !== 'ConnectionEstablished';

  const [{ fetching: isEstablishing }, establishConnection] = useAuthMutation(
    JoeEstablishConnectionDocument,
  );
  const [{ fetching: isDisconnecting }, deleteConnection] = useAuthMutation(
    JoeDeleteConnectionDocument,
  );

  const [hasEstablishFailed, setHasEstablishFailed] = useState(false);
  // The state draws the button; the ref guards re-entry, like `isDisconnectInFlight` below.
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isRedirectInFlight = useRef(false);
  const [isReturnPending, setIsReturnPending] = useState(false);
  const [hasCheckedReturn, setHasCheckedReturn] = useState(false);
  const { isHydrated } = useIsHydrated();

  /**
   * Flags a returning round-trip on the first client render, before any query can resolve, so the
   * card cannot show the pre-connect status while the establish call is still waiting for the
   * token and the eligibility flag. Waits for hydration so server rendering stays untouched.
   */
  if (isHydrated && !hasCheckedReturn) {
    setHasCheckedReturn(true);
    if (parseAuthorizationCode(window.location.search)) {
      setIsReturnPending(true);
    }
  }

  /**
   * The login url is prefetched once the card is actually offering the connect action, so the click
   * can redirect straight away. Fetching it earlier would spend a request on every overview view.
   */
  const [{ data: loginUrlData, fetching: isLoginUrlFetching }] = useAuthQuery({
    query: JoeLoginUrlDocument,
    pause: !isLinkingAllowed || !isDisconnected,
  });

  const loginUrlResult = loginUrlData?.customerLoyaltyLoginUrl;
  const loginUrl =
    loginUrlResult?.__typename === 'CustomerLoyaltyLoginUrlPayload'
      ? loginUrlResult.url
      : undefined;

  // The customer is refetched too: connecting and disconnecting flip `loyaltyIntegrationAllowed`,
  // and a stale `false` after a disconnect would hide the connect card.
  const refreshStatus = useCallback(() => {
    refetchStatus({ requestPolicy: 'network-only' });
    refetchCustomer({ requestPolicy: 'network-only' });
  }, [refetchStatus, refetchCustomer]);
  const isCustomerRefreshing = isCustomerFetching && customerData !== undefined;

  /**
   * Finishes a returning SSO round-trip. Guarded by a ref as well as by removing the code from the
   * address bar, so neither a re-render nor React's development double-effect can replay a
   * single-use code.
   */
  const hasHandledReturn = useRef(false);

  useEffect(() => {
    // Waits for the token: `useAuthMutation` cannot pause itself, and firing early spends the
    // single-use code on a request the api answers with `UNAUTHENTICATED`. Same for eligibility -
    // an excluded customer must not be linked by a code that outran the flag.
    // `hasCheckedReturn` keeps the code in the address bar until the flag above has read it.
    if (
      !hasCheckedReturn ||
      !isLinkingAllowed ||
      !jwtToken ||
      !isEligibilityResolved ||
      hasHandledReturn.current
    ) {
      return;
    }

    const code = parseAuthorizationCode(window.location.search);

    if (!code) {
      return;
    }

    hasHandledReturn.current = true;
    stripAuthorizationCode();

    const finish = (hasFailed: boolean) => {
      setHasEstablishFailed(hasFailed);
      setIsReturnPending(false);

      if (hasFailed) {
        return;
      }

      addNotification({
        severity: 'success',
        message: formatMessage(joeMessages.connectSuccess),
        duration: NOTIFICATION_DURATION,
      });
      refreshStatus();
    };

    establishConnection({ code, locale }).then(
      (result) => {
        const payload = result.data?.customerEstablishLoyaltyConnection;
        // Only an actually established connection counts: a status payload still reporting
        // `NoConnection` means the code was accepted but nothing was linked.
        finish(
          Boolean(result.error) ||
            payload?.__typename !== 'CustomerLoyaltyConnectionStatusPayload' ||
            payload.status !== 'ConnectionEstablished',
        );
      },
      // A rejected mutation must still leave the loading state, or the card never resolves.
      () => finish(true),
    );
  }, [
    hasCheckedReturn,
    isLinkingAllowed,
    jwtToken,
    isEligibilityResolved,
    locale,
    establishConnection,
    addNotification,
    formatMessage,
    refreshStatus,
  ]);

  /**
   * Sends the customer to the jö login.
   *
   * The provider is entered through the `joe-connect` hop rather than directly: jö always returns to
   * one fixed callback url, and only the hop can remember that this attempt started here. It hands
   * the customer back to the current path with `?code=`, which the effect above picks up.
   */
  const connect = useCallback(() => {
    if (!loginUrl || isRedirectInFlight.current) {
      return;
    }

    isRedirectInFlight.current = true;
    setIsRedirecting(true);
    redirectExternal(
      buildJoeConnectUrl({
        connectPath: getLocalizedPath(myAccountJoeConnect, locale),
        loginUrl,
        returnTo: window.location.pathname,
      }),
    );
  }, [loginUrl, myAccountJoeConnect, locale]);

  /**
   * Re-entry guard for the delete mutation. The button stays focusable through `aria-disabled`
   * rather than being natively disabled, so a second activation is possible in the same tick -
   * before `isDisconnecting` has propagated - and must be dropped here.
   */
  const isDisconnectInFlight = useRef(false);

  const disconnect = useCallback(() => {
    if (isDisconnectInFlight.current) {
      return;
    }

    isDisconnectInFlight.current = true;

    const finish = (hasFailed: boolean) => {
      isDisconnectInFlight.current = false;

      addNotification({
        severity: hasFailed ? 'error' : 'success',
        message: formatMessage(
          hasFailed ? joeMessages.disconnectError : joeMessages.disconnectSuccess,
        ),
        duration: NOTIFICATION_DURATION,
      });

      if (!hasFailed) {
        refreshStatus();
      }
    };

    deleteConnection({}).then(
      (result) => {
        const payload = result.data?.customerDeleteLoyaltyConnection;
        finish(
          Boolean(result.error) ||
            payload?.__typename !== 'CustomerGenericSuccessResult' ||
            !payload.success,
        );
      },
      // A rejected mutation must still release the guard, or the action stays dead for good.
      () => finish(true),
    );
  }, [deleteConnection, addNotification, formatMessage, refreshStatus]);

  // A refetched flag still describes the connection from before the action until it answers.
  const isGateOpen = isJoeEnabled && (isLoyaltyAllowed || isConnected || isCustomerRefreshing);

  const state = resolveJoeState({
    isGateOpen,
    hasTransportError: Boolean(statusError),
    result: statusResult,
    isStatusRefreshing:
      (isStatusFetching || isStatusStale || isCustomerRefreshing) && Boolean(statusResult),
    isReturnPending,
    isEstablishing,
    hasEstablishFailed,
    notifications: {
      statusError: formatMessage(joeMessages.statusError),
      connectError: formatMessage(joeMessages.connectError),
    },
  });

  return {
    state,
    connect,
    canConnect: Boolean(loginUrl) && !isRedirecting,
    isConnecting: isLoginUrlFetching || isRedirecting,
    disconnect,
    isDisconnecting,
  };
};
